import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Bell, CheckCheck, MapPin, MessageCircle, UserPlus, UserCheck, FileEdit, Vote, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "react-i18next";
import type { Notification } from "@shared/schema";

function getNotificationRoute(notif: Notification): string | null {
  switch (notif.type) {
    case "new_message":
      return "/messages";
    case "connection_request":
    case "friend_accepted":
      return "/friends";
    case "join_request":
    case "trip_accepted":
    case "trip_rejected":
    case "trip_removed":
    case "new_expense":
    case "expense_vote":
    case "itinerary_proposal":
    case "proposal_approved":
    case "proposal_rejected":
    case "price_watch_alert":
      return notif.tripId ? `/trip/${notif.tripId}` : null;
    default:
      return notif.tripId ? `/trip/${notif.tripId}` : null;
  }
}

function getNotificationIcon(type: string) {
  switch (type) {
    case "new_message":
      return MessageCircle;
    case "connection_request":
      return UserPlus;
    case "friend_accepted":
      return UserCheck;
    case "join_request":
    case "trip_accepted":
    case "trip_rejected":
    case "trip_removed":
      return MapPin;
    case "new_expense":
    case "expense_vote":
      return DollarSign;
    case "itinerary_proposal":
    case "proposal_approved":
    case "proposal_rejected":
      return FileEdit;
    default:
      return Bell;
  }
}

export function NotificationBell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();

  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: async () => {
      const res = await fetch("/api/notifications/unread-count", { credentials: "include" });
      if (!res.ok) return 0;
      const data = await res.json();
      return data.count;
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  const { data: notificationsData } = useQuery<{ data: Notification[]; nextCursor: string | null }>({
    queryKey: queryKeys.notifications.all,
    enabled: !!user && open,
  });
  const notifications = notificationsData?.data ?? [];

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/notifications/${id}/read`);
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.unreadCount });
      const previousNotifications = queryClient.getQueryData(queryKeys.notifications.all);
      const previousCount = queryClient.getQueryData<number>(queryKeys.notifications.unreadCount);
      queryClient.setQueryData(queryKeys.notifications.all, (old: { data: Notification[]; nextCursor: string | null } | undefined) =>
        old ? { ...old, data: old.data.map(n => n.id === id ? { ...n, isRead: true } : n) } : old
      );
      queryClient.setQueryData(queryKeys.notifications.unreadCount, (old: number | undefined) =>
        Math.max(0, (old || 0) - 1)
      );
      return { previousNotifications, previousCount };
    },
    onError: (_err, _id, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(queryKeys.notifications.all, context.previousNotifications);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(queryKeys.notifications.unreadCount, context.previousCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/notifications/read-all");
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.unreadCount });
      const previousNotifications = queryClient.getQueryData(queryKeys.notifications.all);
      const previousCount = queryClient.getQueryData<number>(queryKeys.notifications.unreadCount);
      queryClient.setQueryData(queryKeys.notifications.all, (old: { data: Notification[]; nextCursor: string | null } | undefined) =>
        old ? { ...old, data: old.data.map(n => ({ ...n, isRead: true })) } : old
      );
      queryClient.setQueryData(queryKeys.notifications.unreadCount, 0);
      return { previousNotifications, previousCount };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(queryKeys.notifications.all, context.previousNotifications);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(queryKeys.notifications.unreadCount, context.previousCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
    },
  });

  if (!user) return null;

  const formatTime = (date: string | Date | null) => {
    if (!date) return "";
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    return `${diffDays}d ago`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications" data-testid="button-notifications">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground"
              data-testid="badge-unread-count"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">{t("notifications.title")}</h3>
          {notifications.some(n => !n.isRead) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              data-testid="button-mark-all-read"
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{t("notifications.noNotifications")}</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`px-4 py-3 hover-elevate cursor-pointer ${!notif.isRead ? "bg-primary/5" : ""}`}
                  data-testid={`notification-${notif.id}`}
                  onClick={() => {
                    if (!notif.isRead) markReadMutation.mutate(notif.id);
                    const route = getNotificationRoute(notif);
                    if (route) {
                      setOpen(false);
                      navigate(route);
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 flex-shrink-0 h-2 w-2 rounded-full ${!notif.isRead ? "bg-primary" : "bg-transparent"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{notif.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{notif.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatTime(notif.createdAt)}</p>
                    </div>
                    {(() => {
                      const NotifIcon = getNotificationIcon(notif.type);
                      return <NotifIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-1" />;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
